import { useState, useRef } from 'react';
import dayjs from '../../Abstracts/Time';

interface PhotoCardProps {
	photo: {
		clientUploadId: number;
		clientUploadFilename: string;
		clientUploadTime: number;
		clientUploadSize: number;
	};
	hash: string;
	isSelected: boolean;
	isSelectMode: boolean;
	onToggleSelection: (photoId: number) => void;
	onView: () => void;
	onDownload: () => void;
	onDelete: () => void;
}

export default function PhotoCard(props: PhotoCardProps) {
	const { photo, hash, isSelected, isSelectMode, onToggleSelection, onView, onDownload, onDelete } = props;
	const [imageError, setImageError] = useState(false);
	const longPressTimer = useRef<number | null>(null);
	const [isLongPressing, setIsLongPressing] = useState(false);

	const handleClick = () => {
		// Desktop: Single click toggles selection
		// Mobile: Single click opens lightbox (selection only via long-press)
		const isMobile = window.innerWidth <= 768;

		if (isMobile && !isSelectMode) {
			// On mobile, click opens lightbox unless already in select mode
			onView();
		} else {
			// On desktop or when in select mode, click toggles selection
			onToggleSelection(photo.clientUploadId);
		}
	};

	const handleDoubleClick = () => {
		// Double click opens lightbox view
		onView();
	};

	// Long press for mobile selection
	const handleTouchStart = () => {
		setIsLongPressing(true);
		longPressTimer.current = setTimeout(() => {
			if (!isSelectMode) {
				onToggleSelection(photo.clientUploadId);
				// Haptic feedback if available
				if ('vibrate' in navigator) {
					navigator.vibrate(50);
				}
			}
		}, 500); // 500ms long press
	};

	const handleTouchEnd = () => {
		setIsLongPressing(false);
		if (longPressTimer.current) {
			clearTimeout(longPressTimer.current);
			longPressTimer.current = null;
		}
	};

	const formatFileSize = (bytes: number): string => {
		if (bytes === 0) return '0 B';
		const k = 1024;
		const sizes = ['B', 'KB', 'MB', 'GB'];
		const i = Math.floor(Math.log(bytes) / Math.log(k));
		return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
	};

	const formatRelativeTime = (timestamp: number): string => {
		return dayjs.unix(timestamp).fromNow();
	};

	return (
		<div
			className={`photo-card ${isSelected ? 'selected' : ''} ${isLongPressing ? 'long-pressing' : ''}`}
			onClick={handleClick}
			onDoubleClick={handleDoubleClick}
			onTouchStart={handleTouchStart}
			onTouchEnd={handleTouchEnd}
			onTouchCancel={handleTouchEnd}
		>
			{(isSelectMode || isSelected) && (
				<div className="photo-card-select-checkbox">
					<input
						type="checkbox"
						checked={isSelected}
						onChange={() => onToggleSelection(photo.clientUploadId)}
						onClick={(e) => e.stopPropagation()}
					/>
				</div>
			)}

			<div className="photo-card-image-container">
				{!imageError ? (
					<img
						className="photo-card-image"
						src={`/u/photos/image/${hash}/${photo.clientUploadFilename}`}
						alt={photo.clientUploadFilename}
						loading="lazy"
						onError={() => setImageError(true)}
					/>
				) : (
					<div className="photo-card-image-placeholder">
						<i className="fas fa-image fa-3x"></i>
					</div>
				)}
			</div>

			<div className="photo-card-metadata-overlay">
				<div className="photo-card-meta-info">
					<span className="file-size">{formatFileSize(photo.clientUploadSize)}</span>
					<span className="upload-time">{formatRelativeTime(photo.clientUploadTime)}</span>
				</div>
			</div>

			<div className="photo-card-actions" onClick={(e) => e.stopPropagation()}>
				<button className="btn btn-default btn-sm" onClick={onView} title="View">
					<i className="fas fa-eye"></i>
				</button>
				<button className="btn btn-default btn-sm" onClick={onDownload} title="Download">
					<i className="fas fa-download"></i>
				</button>
				<button className="btn btn-danger btn-sm" onClick={onDelete} title="Delete">
					<i className="fas fa-trash"></i>
				</button>
			</div>
		</div>
	);
}
